import { useCallback, useRef } from 'react';

import { Box } from 'ui/Layout';

import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { useSankeyInteractionContext } from '../../contexts/sankeyInteractionContext';
import { hasPressedUndoOrRedo } from '../../utils/keyboard';
import { useDebouncedResizeObserverFor } from '../../utils/observers';
import ReadOnlySankeyChart from './ReadOnlySankeyChart';
import SVGScratch from './SVGScratch';
import SankeyFooter from './SankeyFooter';
import SankeySVG from './SankeySVG';

const InteractiveSankeyChart: React.FC = () => {
  const { blurFocusedElement } = useSankeyInteractionContext();
  const { isFetching } = useSankeyFormContext();

  const svgRef = useRef<HTMLDivElement>(null);
  const svgSize = useDebouncedResizeObserverFor(svgRef);

  // Capture undo and redo keyboard shortcuts when interacting with the chart and use
  // them to blur the focused element so we don't have to worry about updating active
  // popup's input elements downstream in the component tree and keep the code simple!
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (hasPressedUndoOrRedo(event)) {
        blurFocusedElement();
      }
    },
    [blurFocusedElement]
  );

  if (isFetching) {
    return <ReadOnlySankeyChart />;
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.300"
      id="chart"
      overflow="hidden"
      position="relative"
      onKeyDown={handleKeyDown}
    >
      <SVGScratch />
      <Box ref={svgRef}>
        <SankeySVG />
      </Box>
      <SankeyFooter svgSize={svgSize} />
    </Box>
  );
};

export default InteractiveSankeyChart;
